import { useMemo } from 'react';
import t from 'react-hot-toast';
import { NotificationType } from '../../../typings/notifications.js';
import { getToastStyles } from '../ToasterStyles.js';
import { jsx } from '@emotion/react/jsx-runtime';
import { Toast } from '../../Toast/Toast.js';

const defaultTranslation = key => {
  switch (key) {
    case 'toast.action.close':
      return 'Close';
    case 'toast.title.success':
      return 'Great! everything went well';
    case 'toast.title.info':
      return 'Did you know?';
    case 'toast.title.alert':
      return 'Oops... something went wrong';
    case 'toast.title.error':
      return 'An error has occurred';
    case 'toast.title.loading':
      return 'Action in progress...';
    default:
      return key;
  }
};

/**
 *
 * @param translate Translate function
 *
 * The component uses the following translations:
 *  - `toast.action.close` -> Close
 *
 *  Default titles:
 *  - `toast.title.success` -> Great! everything went well
 *  - `toast.title.info` -> Did you know?
 *  - `toast.title.alert` -> Oops... something went wrong
 *  - `toast.title.error` -> An error has occurred
 */
function useToast() {
  let translate = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultTranslation;
  const handlers = useMemo(() => ({
    success: toastOptions => createToast(NotificationType.SUCCESS, toastOptions, translate),
    info: toastOptions => createToast(NotificationType.INFO, toastOptions, translate),
    alert: toastOptions => createToast(NotificationType.ALERT, toastOptions, translate),
    error: toastOptions => createToast(NotificationType.ERROR, toastOptions, translate),
    loading: toastOptions => createLoadingToast(toastOptions, translate),
    dismiss: t.dismiss,
    remove: t.remove
  }), [translate]);
  return handlers;
}
function closeToast(toast, cb) {
  t.dismiss(toast.id);
  cb === null || cb === void 0 ? void 0 : cb();
}
function createToast(type, _ref, translate) {
  let {
    id,
    duration,
    position,
    ...toastProps
  } = _ref;
  const toastDuration = type === NotificationType.LOADING ? duration !== null && duration !== void 0 ? duration : Infinity : duration;
  return t.custom(toast => {
    return jsx(Toast, {
      type: type,
      css: getToastStyles,
      closeText: translate('toast.action.close'),
      title: translate(`toast.title.${type}`),
      ...toastProps,
      onClose: () => closeToast(toast, toastProps.onClose),
      children: toastProps.message
    });
  }, {
    id,
    duration: toastDuration,
    position
  });
}
function createLoadingToast(_ref2, translate) {
  let {
    id,
    duration,
    position,
    promise,
    success,
    error,
    ...toastProps
  } = _ref2;
  const {
    onClose,
    closeText,
    ...props
  } = toastProps;
  const toastDuration = duration !== null && duration !== void 0 ? duration : Infinity;
  const type = NotificationType.LOADING;
  const loadingId = t.custom(() => {
    return jsx(Toast, {
      type: type,
      css: getToastStyles,
      title: translate(`toast.title.${type}`),
      ...props,
      children: toastProps.message
    });
  }, {
    id,
    duration: toastDuration,
    position
  });
  return {
    id: loadingId,
    promise: promise === null || promise === void 0 ? void 0 : promise.then(p => {
      t.custom(toast => {
        const type = NotificationType.SUCCESS;
        return jsx(Toast, {
          type: type,
          css: getToastStyles,
          closeText: translate('toast.action.close'),
          title: translate(`toast.title.${type}`),
          ...success,
          onClose: () => closeToast(toast, success === null || success === void 0 ? void 0 : success.onClose),
          children: typeof (success === null || success === void 0 ? void 0 : success.message) === 'function' ? success.message(p) : success === null || success === void 0 ? void 0 : success.message
        });
      }, {
        id: loadingId,
        duration,
        position
      });
      return p;
    }).catch(e => {
      t.custom(toast => {
        const type = NotificationType.ERROR;
        return jsx(Toast, {
          type: type,
          css: getToastStyles,
          closeText: translate('toast.action.close'),
          title: translate(`toast.title.${type}`),
          ...error,
          onClose: () => closeToast(toast, error === null || error === void 0 ? void 0 : error.onClose),
          children: typeof (error === null || error === void 0 ? void 0 : error.message) === 'function' ? error.message(e) : error === null || error === void 0 ? void 0 : error.message
        });
      }, {
        id: loadingId,
        duration,
        position
      });
      throw e;
    })
  };
}

export { useToast };
//# sourceMappingURL=useToast.js.map
